import { useState, useCallback, useRef } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandInput,
	CommandItem,
	CommandList,
} from "@/components/ui/command";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { useCustomerSearchGraphQL } from "@/hooks/useCustomerSearchGraphQL";
import type { CreatePromiseFormCustomer } from "@/hooks/useCreatePromiseData";

const DEBOUNCE_DELAY_MS = 300;

function formatCustomerLabel(accountNumber: string, name: string): string {
	return name ? `${accountNumber} - ${name}` : accountNumber;
}

export interface CustomerSearchComboboxProps {
	/** The currently selected customer, or null if none selected */
	selectedCustomer: CreatePromiseFormCustomer | null;
	/** Called when a customer is selected from the dropdown */
	onSelectCustomer: (customer: CreatePromiseFormCustomer) => void;
	/** Whether the combobox is disabled */
	disabled?: boolean;
	/** Placeholder text shown when no customer is selected */
	placeholder?: string;
	/** Additional CSS classes for the trigger button */
	className?: string;
}

export function CustomerSearchCombobox({
	selectedCustomer,
	onSelectCustomer,
	disabled = false,
	placeholder = "Select a customer...",
	className,
}: Readonly<CustomerSearchComboboxProps>) {
	const [open, setOpen] = useState(false);
	const [searchInput, setSearchInput] = useState("");
	const [debouncedSearch, setDebouncedSearch] = useState("");
	const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const {
		customers,
		isLoading,
		isFetchingMore,
		hasMore,
		loadMore,
	} = useCustomerSearchGraphQL(debouncedSearch);

	const handleSearchChange = useCallback((value: string) => {
		setSearchInput(value);
		if (debounceTimerRef.current) {
			clearTimeout(debounceTimerRef.current);
		}
		debounceTimerRef.current = setTimeout(() => {
			setDebouncedSearch(value);
		}, DEBOUNCE_DELAY_MS);
	}, []);

	const handleOpenChange = useCallback(
		(nextOpen: boolean) => {
			setOpen(nextOpen);
			if (nextOpen) {
				setSearchInput("");
				setDebouncedSearch("");
			}
		},
		[],
	);

	const handleSelect = useCallback(
		(customerId: string) => {
			const customer = customers.find((c) => c.id === customerId);
			if (customer) {
				onSelectCustomer(customer);
				setOpen(false);
				setSearchInput("");
				setDebouncedSearch("");
			}
		},
		[customers, onSelectCustomer],
	);

	const buttonText = selectedCustomer
		? formatCustomerLabel(selectedCustomer.accountNumber, selectedCustomer.name)
		: placeholder;

	return (
		<Popover open={open} onOpenChange={handleOpenChange}>
			<PopoverTrigger asChild>
				<Button
					variant="outline"
					aria-expanded={open}
					disabled={disabled}
					className={cn(
						"w-full justify-between h-9 font-normal",
						className,
					)}
				>
					<span className="truncate min-w-0 flex-1" title={buttonText}>
						{buttonText}
					</span>
					<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
				</Button>
			</PopoverTrigger>
			<PopoverContent
				className="w-[--radix-popover-trigger-width] p-0"
				align="start"
			>
				<Command shouldFilter={false}>
					<CommandInput
						placeholder="Search customers..."
						value={searchInput}
						onValueChange={handleSearchChange}
					/>
					<CommandList>
						{!isLoading && customers.length === 0 && (
							<CommandEmpty>No customer found.</CommandEmpty>
						)}
						<CommandGroup>
							{customers.map((customer) => (
								<CommandItem
									key={customer.id}
									value={customer.id}
									onSelect={() => handleSelect(customer.id)}
								>
										<Check
										className={cn(
											"mr-2 h-4 w-4",
											selectedCustomer?.id === customer.id
												? "opacity-100"
												: "opacity-0",
										)}
									/>
									<span className="truncate" title={formatCustomerLabel(customer.accountNumber, customer.name)}>
										{formatCustomerLabel(customer.accountNumber, customer.name)}
									</span>
								</CommandItem>
							))}
							{isLoading && (
								<CommandItem disabled value="__loading__">
									<span className="text-muted-foreground text-xs">
										Loading...
									</span>
								</CommandItem>
							)}
							{hasMore && !isLoading && (
								<CommandItem
									value="__load_more__"
									onSelect={loadMore}
									className="justify-center"
								>
									<span className="text-xs font-medium text-primary">
										{isFetchingMore ? "Loading..." : "Load more"}
									</span>
								</CommandItem>
							)}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
}
